import React, { CSSProperties, ReactNode, useMemo } from "react";
import MultiItemSelect from "common/Filters/common/MultiItemSelect";
import { FilterSectionData } from "@shared/features/filters/types";
import { v4 as uuid } from "uuid";
import pluralize from "pluralize";
import { getAllSelectedDisplay } from "@shared/features/filters/utils";
import {
  CheckboxFilterType,
  FilterSectionType,
  FilterType,
} from "@shared/types";
import Text from "common/Typography/Text";
import useMeasure from "utils/hooks/useMeasure";
import Rate from "common/Rate/Rate";
import { useBottomNavigator } from "utils/hooks/misc";
import { MobileResetButton } from "common/Filters/common/styles";

const MultiSelectFiltersSection = <T extends CheckboxFilterType>({
  filterType,
  title,
  onChange,
  value,
  options,
  displayData,
  isSingleRowView,
  isChipsView,
  getChipsText,
  filterSectionType,
  resetButtonData,
}: FilterSectionData<T>) => {
  const containerID = useMemo(uuid, []);
  const [measureRef, { width }] = useMeasure<HTMLDivElement>();
  const isSymbolsFilter = filterType === FilterType.SYMBOLS;
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  const isMultiple = filterSectionType !== FilterSectionType.RADIO;

  let allSelected = false;
  if (value?.length === options?.length) {
    allSelected = true;
  }

  const getPlaceholder = () => {
    if (isChipsView && isSingleRowView) return getChipsText();
    return allSelected ? getAllSelectedDisplay(title, isSingleRowView) : "";
  };

  let selectStyle: CSSProperties = { height: "100%" };
  if (isSingleRowView) {
    selectStyle = {
      ...selectStyle,
      minWidth: Math.max(120, width),
      maxWidth: 200,
    };
  }

  const placeholder = isSingleRowView ? (
    <div ref={measureRef}>
      <Text
        type={isSingleRowView ? "primary" : "secondary"}
        style={{ padding: "0 12px" }}
      >
        {getPlaceholder()}
      </Text>
    </div>
  ) : (
    getPlaceholder()
  );

  const getOptionDisplay = (option: string | number) => {
    let optionDisplay: string | number | ReactNode = option;
    if (filterType === FilterType.AVG_RATING) {
      if (option === -1) optionDisplay = "Unrated";
      else {
        optionDisplay = (
          <Rate
            disabled
            defaultValue={option as number}
            style={{ fontSize: 13 }}
          />
        );
      }
    }
    return optionDisplay;
  };

  const select = useMemo(() => {
    if (options && value) {
      return (
        <MultiItemSelect
          allItems={options as string[]}
          selectedItems={
            !isBottomNavigator && allSelected ? [] : (value as string[])
          }
          onChange={onChange}
          placeholder={placeholder}
          disabled={options.length === 0}
          getIconProps={displayData.getIconProps}
          getAccountId={displayData.getAccountId}
          containerID={
            isBottomNavigator && isChipsView ? undefined : containerID
          }
          style={selectStyle}
          getOverflowTagText={(numSelected) =>
            allSelected ? getPlaceholder() : pluralize(title, numSelected, true)
          }
          popupMatchSelectWidth={isSymbolsFilter && !isSingleRowView}
          renderOption={displayData.getDisplay ?? getOptionDisplay}
          isMultiple={isMultiple}
          isSingleRowView={isSingleRowView}
          width={width}
          title={title}
          allSelected={allSelected}
          bottomExtraElement={
            isBottomNavigator && isSingleRowView && isMultiple ? (
              <MobileResetButton onClick={resetButtonData.onClick}>
                {resetButtonData.resetText}
              </MobileResetButton>
            ) : undefined
          }
        />
      );
    }
    return null;
  }, [options, value, width, isSingleRowView]);

  const style: CSSProperties = {};
  if (filterType === FilterType.SYMBOLS && !isSingleRowView) {
    style.minWidth = 180;
  }
  if (!isChipsView) {
    style.marginBottom = 8;
  }

  return (
    <div id={containerID} style={style}>
      {select}
    </div>
  );
};

export default MultiSelectFiltersSection;
